import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Cookie } from "lucide-react";
import { useConsent } from "@/contexts/ConsentContext";
import { useClient } from "@/contexts/ClientContext";
import { translations, DEFAULT_CONSENT } from "@/config/consent";
import { CookiePreferencesModal } from "./CookiePreferencesModal";
import { toast } from "sonner";

export const CookieBanner = () => {
  const { setConsent, needsConsent, locale } = useConsent();
  const { client } = useClient();
  const [showModal, setShowModal] = useState(false);
  const t = translations[locale].banner;

  // Don't show banner until client is loaded
  if (!needsConsent || !client) {
    return null;
  }

  const handleAcceptAll = () => {
    setConsent({
      version: DEFAULT_CONSENT.version,
      choices: {
        essential: true,
        analytics: true,
        product_improvement: true,
        marketing: true,
      },
      timestamp: new Date().toISOString(),
      locale,
    });
    toast.success(translations[locale].modal.successToast);
  };

  const handleRejectAll = () => {
    setConsent({
      version: DEFAULT_CONSENT.version,
      choices: {
        essential: true,
        analytics: false,
        product_improvement: false,
        marketing: false,
      },
      timestamp: new Date().toISOString(),
      locale,
    });
    toast.success(translations[locale].modal.successToast);
  };

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-50 p-4 pb-safe animate-in slide-in-from-bottom-5"
        role="dialog"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-description"
      >
        <Card className="max-w-4xl mx-auto p-4 sm:p-6 shadow-2xl border-2">
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <Cookie className="w-6 h-6 text-primary" />
              </div>
            </div>
            
            <div className="flex-1 space-y-3">
              <h2 id="cookie-banner-title" className="text-lg sm:text-xl font-semibold">
                {t.title}
              </h2>
              <p id="cookie-banner-description" className="text-sm text-muted-foreground leading-relaxed">
                {t.description}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-2 pt-2">
                <Button
                  onClick={handleAcceptAll}
                  className="w-full sm:w-auto"
                  aria-label={t.acceptAll}
                >
                  {t.acceptAll}
                </Button>
                <Button
                  onClick={handleRejectAll}
                  variant="outline"
                  className="w-full sm:w-auto"
                  aria-label={t.rejectAll}
                >
                  {t.rejectAll}
                </Button>
                <Button
                  onClick={() => setShowModal(true)}
                  variant="ghost"
                  className="w-full sm:w-auto"
                  aria-label={t.customize}
                >
                  {t.customize}
                </Button>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <CookiePreferencesModal open={showModal} onOpenChange={setShowModal} />
    </>
  );
};
